import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { invocarFuncion } from '../lib/invocarFuncion'
import { useWorkspace } from '../lib/WorkspaceContext'
import './AdminRecetarios.css'
import './AdminWorkspace.css'
import './marcaManual.css'

const TIPOS = ['desayuno_merienda', 'almuerzo_cena', 'snack', 'postre', 'bebida']
const DULZORES = ['', 'dulce', 'salado', 'neutro', 'agridulce']

const TIPOS_LABEL = {
  desayuno_merienda: 'Desayuno/merienda',
  almuerzo_cena: 'Almuerzo/cena',
  snack: 'Snack',
  postre: 'Postre',
  bebida: 'Bebida'
}

function recetaVacia() {
  return {
    nombre: '', tipo_comida: TIPOS[0], dulzor: '', tiempo_prep_min: '',
    lleva_remojo: false, ingrediente_remojo: '', pasos: '', notas: '', rendimiento: '',
    apta_sin_gluten: false, apta_vegana: false, ingredientes: []
  }
}

const ESTADO_LABEL = {
  pendiente: '⏳ Pendiente',
  procesando: '🤖 Procesando...',
  revision: '📝 Para revisar',
  publicado: '✓ Publicado',
  error: '⚠️ Error'
}

export default function AdminRecetarios({ usuario }) {
  const { workspaceId } = useWorkspace()
  const [importaciones, setImportaciones] = useState([])
  const [recetasPublicadas, setRecetasPublicadas] = useState([])
  const [loading, setLoading] = useState(true)
  const [subiendo, setSubiendo] = useState(false)
  const [activa, setActiva] = useState(null)
  const [ingredientesExistentes, setIngredientesExistentes] = useState([])
  const [publicando, setPublicando] = useState(false)
  const [erroresRecetas, setErroresRecetas] = useState({})
  const [recetariosCount, setRecetariosCount] = useState(0)
  const [busquedaRecetas, setBusquedaRecetas] = useState('')
  const inputFileRef = useRef(null)

  useEffect(() => {
    cargarImportaciones()
    cargarRecetasPublicadas()
    cargarIngredientes()
    cargarRecetariosCount()
  }, [workspaceId])

  useEffect(() => {
    const hayProcesando = importaciones.some(i => i.estado === 'procesando')
    if (!hayProcesando) return
    const interval = setInterval(cargarImportaciones, 3000)
    return () => clearInterval(interval)
  }, [importaciones])

  const cargarImportaciones = async () => {
    if (!workspaceId) return
    const { data } = await supabase.from('importaciones_recetario').select('*').eq('workspace_id', workspaceId).order('created_at', { ascending: false })
    setImportaciones(data || [])
    setLoading(false)
  }

  // #1 (QA 23/08): las cargas manuales nunca pasan por "importaciones_recetario"
  // (ver comentario en publicar() más abajo), así que sin este listado propio
  // una receta manual quedaba invisible después de publicarla.
  const cargarRecetasPublicadas = async () => {
    if (!workspaceId) return
    const { data } = await supabase.from('recetas').select('*').eq('workspace_id', workspaceId).order('nombre')
    setRecetasPublicadas(data || [])
  }

  const cargarRecetariosCount = async () => {
    if (!workspaceId) return
    const { count } = await supabase.from('recetarios').select('id', { count: 'exact', head: true }).eq('workspace_id', workspaceId)
    setRecetariosCount(count || 0)
  }

  const cargarIngredientes = async () => {
    if (!workspaceId) return
    const { data } = await supabase.from('ingredientes').select('id, nombre').or(`workspace_id.is.null,workspace_id.eq.${workspaceId}`)
    setIngredientesExistentes(data || [])
  }

  const subirPDF = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setSubiendo(true)
    const path = `${crypto.randomUUID()}-${file.name}`

    const { error: uploadError } = await supabase.storage.from('recetarios-fuente').upload(path, file)
    if (uploadError) { alert('No se pudo subir el archivo.'); setSubiendo(false); return }

    const { data: importacion, error: insertError } = await supabase
      .from('importaciones_recetario')
      .insert({ archivo_nombre: file.name, archivo_path: path, creado_por: usuario.id, workspace_id: workspaceId })
      .select().single()

    if (insertError || !importacion) { alert('No se pudo registrar la importación.'); setSubiendo(false); return }

    await cargarImportaciones()
    setSubiendo(false)
    procesar(importacion.id)
  }

  // BUG CONOCIDO (encontrado 25/07/2026, no arreglado a propósito — la
  // feature está pausada/oculta, ver botón de subida más abajo): esta
  // función invoca extraer-recetario, que nunca maneja el preflight
  // OPTIONS que manda el navegador por el header Authorization. La Edge
  // Function devuelve 405 sin headers de CORS, el navegador aborta el
  // POST real antes de mandarlo, y la importación queda trabada en
  // estado 'pendiente' para siempre, sin ningún log ni error visible.
  // Mismo bug que tenía eliminar-cuenta (ya arreglado ahí, ver
  // supabase/functions/eliminar-cuenta/index.ts) — el fix es agregar
  // manejo de OPTIONS + headers de CORS en todas las respuestas de
  // supabase/functions/extraer-recetario/index.ts antes de reactivar esto.
  const procesar = async (importacionId) => {
    await invocarFuncion('extraer-recetario', { body: { importacion_id: importacionId } })
    cargarImportaciones()
  }

  const abrirCargaManual = () => {
    setActiva({
      id: null,
      esManual: true,
      archivo_nombre: 'Carga manual',
      edit: { recetario: { nombre: '', descripcion: '' }, recetas: [recetaVacia()] }
    })
    setErroresRecetas({})
  }

  const agregarReceta = () => {
    setActiva(prev => ({ ...prev, edit: { ...prev.edit, recetas: [...prev.edit.recetas, recetaVacia()] } }))
    setErroresRecetas({})
  }

  const abrirRevision = (importacion) => {
    const edit = JSON.parse(JSON.stringify(importacion.resultado_json))
    edit.recetas = edit.recetas.map(r => ({
      ...r,
      ingredientes: (r.ingredientes || []).map(i => ({ ...i, es_opcional: !!i.es_opcional, es_principal: !!i.es_principal }))
    }))
    setActiva({ ...importacion, edit })
    setErroresRecetas({})
  }

  const elegirFotoRecetario = (file) => {
    setActiva(prev => ({ ...prev, edit: { ...prev.edit, recetario: { ...prev.edit.recetario, portadaFile: file } } }))
  }

  const elegirFotoReceta = (idx, file) => {
    setActiva(prev => {
      const recetas = [...prev.edit.recetas]
      recetas[idx] = { ...recetas[idx], fotoFile: file }
      return { ...prev, edit: { ...prev.edit, recetas } }
    })
  }

  const subirFotoContenido = async (file, path) => {
    const { error } = await supabase.storage.from('recetas-fotos').upload(path, file, { upsert: true })
    if (error) throw new Error('No se pudo subir la foto')
    const { data: { publicUrl } } = supabase.storage.from('recetas-fotos').getPublicUrl(path)
    return publicUrl
  }

  const actualizarReceta = (idx, campo, valor) => {
    setActiva(prev => {
      const recetas = [...prev.edit.recetas]
      recetas[idx] = { ...recetas[idx], [campo]: valor }
      return { ...prev, edit: { ...prev.edit, recetas } }
    })
    setErroresRecetas(prev => {
      if (!prev[idx]?.[campo]) return prev
      const copia = { ...prev, [idx]: { ...prev[idx], [campo]: false } }
      return copia
    })
  }

  const actualizarIngrediente = (recetaIdx, ingIdx, campo, valor) => {
    setActiva(prev => {
      const recetas = [...prev.edit.recetas]
      const ingredientes = [...recetas[recetaIdx].ingredientes]
      ingredientes[ingIdx] = { ...ingredientes[ingIdx], [campo]: valor }
      recetas[recetaIdx] = { ...recetas[recetaIdx], ingredientes }
      return { ...prev, edit: { ...prev.edit, recetas } }
    })
  }

  const eliminarReceta = (idx) => {
    setActiva(prev => ({ ...prev, edit: { ...prev.edit, recetas: prev.edit.recetas.filter((_, i) => i !== idx) } }))
    setErroresRecetas({})
  }

  const eliminarIngrediente = (recetaIdx, ingIdx) => {
    setActiva(prev => {
      const recetas = [...prev.edit.recetas]
      recetas[recetaIdx] = { ...recetas[recetaIdx], ingredientes: recetas[recetaIdx].ingredientes.filter((_, i) => i !== ingIdx) }
      return { ...prev, edit: { ...prev.edit, recetas } }
    })
  }

  const agregarIngrediente = (recetaIdx) => {
    setActiva(prev => {
      const recetas = [...prev.edit.recetas]
      recetas[recetaIdx] = {
        ...recetas[recetaIdx],
        ingredientes: [...recetas[recetaIdx].ingredientes, { nombre: '', cantidad: '', unidad_normalizada: '', es_opcional: false, es_principal: false }]
      }
      return { ...prev, edit: { ...prev.edit, recetas } }
    })
  }

  const buscarOCrearIngrediente = async (nombre, listaLocal) => {
    const nombreNorm = nombre.trim().toLowerCase()
    const existente = listaLocal.find(i => i.nombre.trim().toLowerCase() === nombreNorm)
    if (existente) return existente.id

    const { data, error } = await supabase.from('ingredientes').insert({ nombre: nombre.trim(), categoria: 'almacen', workspace_id: workspaceId }).select().single()
    if (error || !data) throw new Error(`No se pudo crear el ingrediente "${nombre}"`)
    listaLocal.push({ id: data.id, nombre: data.nombre })
    return data.id
  }

  const publicar = async () => {
    const { recetario, recetas } = activa.edit
    const errores = {}
    recetas.forEach((r, i) => {
      const e = {}
      if (!r.nombre.trim()) e.nombre = true
      if (!r.pasos.trim()) e.pasos = true
      if (Object.keys(e).length > 0) errores[i] = e
    })
    if (Object.keys(errores).length > 0) {
      setErroresRecetas(errores)
      alert(`Falta nombre o pasos en ${Object.keys(errores).length > 1 ? 'algunas recetas' : 'una receta'}. Completá los campos marcados en rojo antes de publicar.`)
      return
    }
    setErroresRecetas({})

    setPublicando(true)
    try {
      // #25: el recetario es opcional — sin nombre, las recetas se publican
      // sueltas, sin fila en "recetarios" ni en "receta_recetarios".
      let recetarioRow = null
      if (recetario.nombre.trim()) {
        const { data, error: recError } = await supabase
          .from('recetarios')
          .insert({ nombre: recetario.nombre, descripcion: recetario.descripcion || null, workspace_id: workspaceId })
          .select().single()
        if (recError || !data) throw new Error('No se pudo crear el recetario')
        recetarioRow = data

        if (recetario.portadaFile) {
          const path = `${workspaceId}/recetario-${recetarioRow.id}-${recetario.portadaFile.name}`
          const portadaUrl = await subirFotoContenido(recetario.portadaFile, path)
          await supabase.from('recetarios').update({ portada_url: portadaUrl }).eq('id', recetarioRow.id)
        }
      }

      const listaIngredientes = [...ingredientesExistentes]

      for (const receta of recetas) {
        const { data: recetaRow, error: recetaError } = await supabase.from('recetas').insert({
          nombre: receta.nombre,
          tipo_comida: receta.tipo_comida,
          dulzor: receta.dulzor || null,
          tiempo_prep_min: receta.tiempo_prep_min || null,
          lleva_remojo: !!receta.lleva_remojo,
          ingrediente_remojo: receta.ingrediente_remojo || null,
          pasos: receta.pasos,
          notas: receta.notas || null,
          rendimiento: receta.rendimiento || null,
          apta_sin_gluten: !!receta.apta_sin_gluten,
          apta_vegana: !!receta.apta_vegana,
          workspace_id: workspaceId
        }).select().single()
        if (recetaError || !recetaRow) throw new Error(`No se pudo crear la receta "${receta.nombre}"`)

        if (receta.fotoFile) {
          const path = `${workspaceId}/receta-${recetaRow.id}-${receta.fotoFile.name}`
          const fotoUrl = await subirFotoContenido(receta.fotoFile, path)
          await supabase.from('recetas').update({ foto_url: fotoUrl }).eq('id', recetaRow.id)
        }

        if (recetarioRow) {
          await supabase.from('receta_recetarios').insert({ receta_id: recetaRow.id, recetario_id: recetarioRow.id })
        }

        for (const ing of receta.ingredientes) {
          if (!ing.nombre?.trim()) continue
          const ingredienteId = await buscarOCrearIngrediente(ing.nombre, listaIngredientes)
          await supabase.from('receta_ingredientes').insert({
            receta_id: recetaRow.id,
            ingrediente_id: ingredienteId,
            cantidad: ing.cantidad || null,
            unidad_normalizada: ing.unidad_normalizada || null,
            es_opcional: !!ing.es_opcional,
            es_principal: !!ing.es_principal
          })
        }
      }

      // Cargas manuales no tienen fila propia en importaciones_recetario —
      // esa tabla sigue significando "pasó por el pipeline de PDF", no se
      // crea una fila artificial solo para reusar este mismo estado final.
      if (!activa.esManual) {
        await supabase.from('importaciones_recetario').update({ estado: 'publicado' }).eq('id', activa.id)
      }
      setIngredientesExistentes(listaIngredientes)
      setActiva(null)
      cargarImportaciones()
      cargarRecetasPublicadas()
      cargarRecetariosCount()
    } catch (err) {
      alert(err.message || 'Error al publicar')
    }
    setPublicando(false)
  }

  if (loading) return <div className="loading">Cargando...</div>

  if (activa) {
    return (
      <div className="admin-recetarios-page">
        <div className="admin-recetarios-header">
          <button className="btn-secundario" onClick={() => setActiva(null)}>← Volver</button>
          <h2 className="clases-modulo-titulo marca-titulo">{activa.esManual ? 'Nueva carga manual' : `Revisar: ${activa.archivo_nombre}`}</h2>
        </div>

        <div className="admin-recetarios-contenido">
          {activa.edit.recetas.map((receta, i) => (
            <div key={i} className="admin-receta-card">
              <div className="admin-receta-card-header">
                <input className={`form-input ${erroresRecetas[i]?.nombre ? 'input-error' : ''}`} placeholder="Título de la receta *" value={receta.nombre} onChange={e => actualizarReceta(i, 'nombre', e.target.value)} />
                <button className="admin-btn-eliminar" onClick={() => eliminarReceta(i)}>✕</button>
              </div>
              {erroresRecetas[i]?.nombre && <p className="campo-error-msg">Falta el título.</p>}

              <label className="form-label">Foto del plato — opcional</label>
              <p className="admin-recetarios-ayuda">Foto de esta receta puntual (distinta de la de portada del recetario).</p>
              <input type="file" accept="image/*" onChange={e => elegirFotoReceta(i, e.target.files[0] || null)} />
              {receta.fotoFile && <p className="admin-recetarios-contador">📷 {receta.fotoFile.name}</p>}

              <div className="form-grid-2">
                <div>
                  <label className="form-label">Tipo de comida</label>
                  <select className="form-input" value={receta.tipo_comida} onChange={e => actualizarReceta(i, 'tipo_comida', e.target.value)}>
                    {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Dulzor</label>
                  <select className="form-input" value={receta.dulzor || ''} onChange={e => actualizarReceta(i, 'dulzor', e.target.value)}>
                    {DULZORES.map(d => <option key={d} value={d}>{d || '—'}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Tiempo (min)</label>
                  <input className="form-input" type="number" value={receta.tiempo_prep_min || ''} onChange={e => actualizarReceta(i, 'tiempo_prep_min', e.target.value ? parseInt(e.target.value) : null)} />
                </div>
                <div>
                  <label className="form-label">Rendimiento</label>
                  <input className="form-input" placeholder="Ej: 4 porciones" value={receta.rendimiento || ''} onChange={e => actualizarReceta(i, 'rendimiento', e.target.value)} />
                  <p className="admin-recetarios-ayuda">Cantidad de porciones que salen de la receta.</p>
                </div>
              </div>

              <div className="admin-receta-checks">
                <label><input type="checkbox" checked={!!receta.apta_vegana} onChange={e => actualizarReceta(i, 'apta_vegana', e.target.checked)} /> Vegana</label>
                <label><input type="checkbox" checked={!!receta.apta_sin_gluten} onChange={e => actualizarReceta(i, 'apta_sin_gluten', e.target.checked)} /> Sin TACC</label>
                <label><input type="checkbox" checked={!!receta.lleva_remojo} onChange={e => actualizarReceta(i, 'lleva_remojo', e.target.checked)} /> Lleva remojo</label>
              </div>
              {receta.lleva_remojo && (
                <input className="form-input" placeholder="Ingrediente que necesita remojo"
                  value={receta.ingrediente_remojo || ''} onChange={e => actualizarReceta(i, 'ingrediente_remojo', e.target.value)} />
              )}

              <label className="form-label">Ingredientes</label>
              {receta.ingredientes.map((ing, j) => (
                <div key={j} className="admin-ingrediente-row">
                  <input className="form-input" placeholder="nombre" value={ing.nombre} onChange={e => actualizarIngrediente(i, j, 'nombre', e.target.value)} />
                  <input className="form-input admin-input-chico" placeholder="cant." inputMode="decimal" value={ing.cantidad || ''} onChange={e => actualizarIngrediente(i, j, 'cantidad', e.target.value.replace(/[^0-9.]/g, ''))} />
                  <input className="form-input admin-input-chico" placeholder="g, cm..." value={ing.unidad_normalizada || ''} onChange={e => actualizarIngrediente(i, j, 'unidad_normalizada', e.target.value)} />
                  <button className="admin-btn-eliminar" onClick={() => eliminarIngrediente(i, j)}>✕</button>
                </div>
              ))}
              <button className="btn-secundario" onClick={() => agregarIngrediente(i)}>+ ingrediente</button>

              <label className="form-label label-requerido">Pasos</label>
              <textarea className={`form-input ${erroresRecetas[i]?.pasos ? 'input-error' : ''}`} rows={4} value={receta.pasos} onChange={e => actualizarReceta(i, 'pasos', e.target.value)} />
              {erroresRecetas[i]?.pasos && <p className="campo-error-msg">Faltan los pasos.</p>}

              <label className="form-label">Notas — opcional</label>
              <textarea className="form-input" rows={2} value={receta.notas || ''} onChange={e => actualizarReceta(i, 'notas', e.target.value)} />
            </div>
          ))}

          <button className="admin-btn-agregar-form" onClick={agregarReceta}>+ Sumar otro formulario de receta a esta carga</button>
          <p className="admin-recetarios-ayuda" style={{ textAlign: 'center', marginTop: -6 }}>
            Todavía no guarda nada — solo agrega otra receta para completar. "Publicar" (más abajo) guarda todo junto.
          </p>

          <div className="admin-receta-card">
            <label className="form-label">Agrupar en un recetario — opcional</label>
            <p className="admin-recetarios-ayuda">Si no le ponés nombre, las recetas se publican sueltas, sin agrupar.</p>
            <input className="form-input" placeholder="Nombre del recetario" value={activa.edit.recetario.nombre}
              onChange={e => setActiva(prev => ({ ...prev, edit: { ...prev.edit, recetario: { ...prev.edit.recetario, nombre: e.target.value } } }))} />
            {activa.edit.recetario.nombre.trim() && (
              <>
                <label className="form-label">Descripción — opcional</label>
                <input className="form-input" value={activa.edit.recetario.descripcion || ''}
                  onChange={e => setActiva(prev => ({ ...prev, edit: { ...prev.edit, recetario: { ...prev.edit.recetario, descripcion: e.target.value } } }))} />
                <label className="form-label">Foto de portada — opcional</label>
                <p className="admin-recetarios-ayuda">Identifica a todo el recetario (esta carga completa), no a cada receta individual.</p>
                <input type="file" accept="image/*" onChange={e => elegirFotoRecetario(e.target.files[0] || null)} />
                {activa.edit.recetario.portadaFile && <p className="admin-recetarios-contador">📷 {activa.edit.recetario.portadaFile.name}</p>}
              </>
            )}
          </div>

          <button className="marca-btn-acento" style={{ width: '100%' }} onClick={publicar} disabled={publicando || activa.edit.recetas.length === 0}>
            {publicando ? 'Publicando...' : `Publicar ${activa.edit.recetas.length} receta${activa.edit.recetas.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    )
  }

  const importacionesPendientes = importaciones.filter(i => i.estado === 'pendiente' || i.estado === 'procesando').length
  const importacionesConError = importaciones.filter(i => i.estado === 'error').length
  const recetasFiltradas = recetasPublicadas.filter(r => r.nombre.toLowerCase().includes(busquedaRecetas.trim().toLowerCase()))

  return (
    <div className="admin-recetarios-page">
      <div className="page-header">
        <h1 className="page-titulo marca-titulo">Recetas</h1>
        <p className="page-subtitulo marca-texto">Catálogo de recetas del espacio</p>
      </div>

      <div className="admin-recetarios-contenido">
        {/* KPIs */}
        <div className="admin-ws-kpis">
          <div className="admin-ws-kpi-card">
            <p className="admin-ws-kpi-numero">{recetasPublicadas.length}</p>
            <p className="admin-ws-kpi-label">Recetas</p>
          </div>
          <div className="admin-ws-kpi-card">
            <p className="admin-ws-kpi-numero">{recetariosCount}</p>
            <p className="admin-ws-kpi-label">Recetarios</p>
          </div>
          <div className="admin-ws-kpi-card">
            <p className="admin-ws-kpi-numero">{importacionesPendientes}</p>
            <p className="admin-ws-kpi-label">Importaciones pendientes</p>
          </div>
          <div className="admin-ws-kpi-card">
            <p className="admin-ws-kpi-numero">{importacionesConError}</p>
            <p className="admin-ws-kpi-label">Importaciones con error</p>
          </div>
        </div>

        <button className="marca-btn-acento" style={{ width: '100%', marginBottom: 12 }} onClick={abrirCargaManual}>
          + Cargar receta manualmente
        </button>

        {/* La importación automática por PDF está pausada (bug de CORS en
            extraer-recetario, ver comentario en procesar() más arriba) — sin
            botón de disparo hasta que se retome. Las importaciones que ya
            existan (pendientes/en revisión/con error) se siguen listando
            abajo para no perder ese historial ni la opción de reintentar. */}
        <input ref={inputFileRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={subirPDF} />

        <h2 className="admin-recetarios-subtitulo">Recetas ({recetasFiltradas.length})</h2>
        {recetasPublicadas.length > 0 && (
          <input
            className="form-input admin-buscador"
            placeholder="Buscar receta..."
            value={busquedaRecetas}
            onChange={e => setBusquedaRecetas(e.target.value)}
            style={{ marginBottom: 4 }}
          />
        )}
        {recetasPublicadas.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🍽</div>
            <p>Todavía no publicaste ninguna receta.</p>
          </div>
        ) : recetasFiltradas.length === 0 ? (
          <p className="admin-recetarios-ayuda">Ninguna receta coincide con "{busquedaRecetas}".</p>
        ) : (
          <div className="admin-ws-tabla-wrap">
            <table className="admin-ws-tabla">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Tiempo</th>
                  <th>Rendimiento</th>
                </tr>
              </thead>
              <tbody>
                {recetasFiltradas.map(receta => (
                  <tr key={receta.id}>
                    <td>{receta.nombre}</td>
                    <td>{TIPOS_LABEL[receta.tipo_comida] || receta.tipo_comida}</td>
                    <td>{receta.tiempo_prep_min ? `${receta.tiempo_prep_min} min` : '—'}</td>
                    <td>{receta.rendimiento || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {importaciones.length > 0 && (
          <>
            <h2 className="admin-recetarios-subtitulo">Importaciones</h2>
            <div className="admin-recetarios-lista">
              {importaciones.map(imp => (
                <div key={imp.id} className="admin-importacion-item" onClick={() => imp.estado === 'revision' && abrirRevision(imp)}>
                  <div>
                    <p className="admin-importacion-nombre">{imp.archivo_nombre}</p>
                    {imp.estado === 'error' && <p className="admin-importacion-error">{imp.error_mensaje}</p>}
                  </div>
                  <div className="admin-importacion-derecha">
                    <span className={`admin-estado-badge admin-estado-${imp.estado}`}>{ESTADO_LABEL[imp.estado]}</span>
                    {imp.estado === 'error' && (
                      <button className="btn-secundario" onClick={(e) => { e.stopPropagation(); procesar(imp.id) }}>Reintentar</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
